import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Field, Form } from 'react-final-form';
import { Button, Loader } from 'ncoded-component-library';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import useCallbackRef from 'hooks/useCallbackRef';
import SideDrawer from '../../../SideDrawer';
import InputField from 'components/InputField';
import validations from 'validations';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import {
  SideDrawerProps,
  SideDrawerRef,
} from '../../../SideDrawer/SideDrawer.component';
import RadioGroupField from 'components/RadioGroupField';
import DatepickerField from 'components/DatepickerField';
import CheckToggleField from 'components/CheckToggleField';
import TextareaEmailsField from 'components/TextareaEmailsField';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import TextAreaField from 'components/TextAreaField';
import PasswordField from 'components/PasswordField';
import useValidations from 'router/subrouters/Auth/hooks/useValidations';
import RadioButtonField from 'components/RadioGroupField/RadioButtonField';
import {
  CreateDataRoomShareableLinkForm,
  DATA_ROOM_SHAREABLE_LINK_ACCESS,
  DATA_ROOM_SHAREABLE_LINK_DESTINATION,
  DATA_ROOM_SHAREABLE_LINK_EXPIRES,
  DataRoomShareableLink,
  ShareDataRoomNodes,
} from 'models/ShareableLink';
import ResolvePopup from 'components/ResolvePopup';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import {
  getArrayOfEmails,
  getDataRoomRoute,
  getHasElementsInArray,
  getIsArrayEmpty,
  getResolveUrl,
  getShareableLinkRoute,
  scrollToErrorsOnSubmit,
} from 'utils';
import {
  createInitialValues,
  createInitialValuesFromLink,
  getPayloadFromFormValues,
  validate,
} from './utils';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import FileInfo from './components/FileInfo';
import ShareableLinkCreated from './components/ShareableLinkCreated';
import {
  getIsAccessAddPasword,
  getIsAccessAddSpecificRecipiens,
  getIsAccessAnyone,
  getIsLinkExpiresOther,
} from 'router/subrouters/DataRoom/utils';
import { useNavigate } from 'react-router-dom';
import ShareableLinkNda from './components/ShareableLinkNda';
import DataRoomNdaContext from 'router/subrouters/DataRoom/providers/DataRoomNda/DataRoomNda.context';
import ShareableLinkTermsAndConditions from './components/ShareableLinkTermsAndConditions';
import { EXPIRE_AFTER } from './data';
import useCreateSecureLinkMixpanelProps from './hook';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';
import ShareableLinkNotifications from './components/ShareableLinkNotifications';

import './ShareableLinkDrawer.styles.scss';

type ShareableLinkDrawerProps = {
  className?: string;
  checkedFileIds?: string[];
  checkedFolderIds?: string[];
  link?: DataRoomShareableLink;
  isEdit?: boolean;
  onFilesReselect: (selectedFileIds: string[]) => Promise<void>;
  updateSecureLinkNodes?: (data: ShareDataRoomNodes) => void;
  onSuccessLink?: (
    link: DataRoomShareableLink,
    dataRoomShareableLinkRequestId?: string,
  ) => void;
} & SideDrawerProps;

const ShareableLinkDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ShareableLinkDrawerProps
> = (props, ref) => {
  const {
    className,
    link,
    isEdit = false,
    checkedFileIds = [],
    checkedFolderIds = [],
    onFilesReselect,
    updateSecureLinkNodes,
    closeAction,
    onSuccessLink,
    ...rest
  } = props;

  const [url, setUrl] = useState('');

  const [isLoadingNodes, setIsLoadingNodes] = useState(false);
  const [nodes, setNodes] = useState<DataRoomNode[]>([]);

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  const classes = classNames('shareable-link-drawer', className);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);
  const {
    dataRoom: { _id: dataRoomId },
    setDataRoom,
  } = useContext(DataRoomContext);
  const { dataRoomNdas } = useContext(DataRoomNdaContext);

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const commonValidations = useValidations();

  const navigate = useNavigate();

  const createSecureLinkMixpanel = useCreateSecureLinkMixpanelProps();

  const title = t(isEdit ? 'editSecureLink' : 'createSecureLink');

  const expireAfterOptions = useMemo(
    () =>
      Object.values(DATA_ROOM_SHAREABLE_LINK_EXPIRES).map((value) => ({
        value,
        label: t(EXPIRE_AFTER[value]),
      })),
    [t],
  );

  const linkDestinationOptions = useMemo(
    () => [
      {
        value: DATA_ROOM_SHAREABLE_LINK_DESTINATION.WAITING_ROOM,
        label: t('waitingRoom'),
      },
      {
        value: DATA_ROOM_SHAREABLE_LINK_DESTINATION.DIRECT_ACCESS,
        label: t('directAccessToFiles'),
      },
    ],
    [t],
  );

  const initialValues = useMemo(
    () =>
      link
        ? createInitialValuesFromLink(link, dataRoomNdas)
        : createInitialValues(),
    [link, dataRoomNdas],
  );

  const isEmpty = useMemo(() => getIsArrayEmpty(nodes), [nodes]);

  const renderForm = useMemo(
    () => (isEdit ? !!link : getHasElementsInArray(checkedFileIds)),
    [checkedFileIds, isEdit, link],
  );

  const onClose = useCallback(() => {
    setNodes([]);
    setUrl('');
    closeAction?.();
  }, [closeAction]);

  const updateTermsAndConditions = useCallback(
    (data: DataRoomShareableLink) => {
      const { includeTermsAndConditionProtection, termsAndConditionsContent } =
        data;

      if (!includeTermsAndConditionProtection) return;

      setDataRoom((old) => ({ ...old, termsAndConditionsContent }));
    },
    [setDataRoom],
  );

  const onSubmit = useCallback(
    async (values) => {
      try {
        const {
          recipientEmailAddresses,
          recipientEmailAddressesUnsubmited,
          dataRoomShareableLinkRequestId,
        } = values;

        const finalEmails = getArrayOfEmails(
          recipientEmailAddresses,
          recipientEmailAddressesUnsubmited,
        );

        const payload = getPayloadFromFormValues({
          ...values,
          recipientEmailAddresses: finalEmails,
        });

        const payloadData = {
          ...payload,
          fileIds: checkedFileIds,
          folderIds: checkedFolderIds,
        };

        if (isEdit) {
          const { data } = await dataRoomApi.shareableLinks.patchShareableLink(
            link?._id,
            payloadData,
          );

          onSuccessLink?.(data);
          updateTermsAndConditions(data);
          popSuccess(t('secureLinkUpdatedSuccessfully'));
          onClose();
          drawer?.setOpen(false);
          return;
        }

        createSecureLinkMixpanel(payloadData);

        const {
          data: {
            dataRoomShareableLink,
            dataRoomShareableLinkRequestId:
              dataRoomShareableLinkRequestIdResponse,
          },
        } = await dataRoomApi.shareableLinks.createShareableLink(dataRoomId, {
          ...payloadData,
          dataRoomShareableLinkRequestId,
        });
        onSuccessLink?.(
          dataRoomShareableLink,
          dataRoomShareableLinkRequestIdResponse,
        );
        updateTermsAndConditions(dataRoomShareableLink);

        setUrl(getResolveUrl(getShareableLinkRoute(dataRoomShareableLink._id)));
      } catch (e) {
        popServerError(e);
      }
    },
    [
      dataRoomId,
      isEdit,
      link,
      drawer,
      checkedFileIds,
      checkedFolderIds,
      t,
      onClose,
      createSecureLinkMixpanel,
      onSuccessLink,
      popServerError,
      popSuccess,
      updateTermsAndConditions,
    ],
  );

  useEffect(() => {
    if (!drawer?.open) return;

    (async () => {
      try {
        const files = [...checkedFileIds, ...checkedFolderIds];

        if (getIsArrayEmpty(files)) return;

        setIsLoadingNodes(true);
        const { data } = await dataRoomApi.nodes.getDataRoomNodesForPreview(
          files,
        );
        setNodes(data);
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
      } finally {
        setIsLoadingNodes(false);
      }
    })();
  }, [drawer, checkedFileIds, checkedFolderIds, getMixpanelProps]);

  return (
    <SideDrawer
      ref={drawerRef}
      className={classes}
      closeAction={onClose}
      {...rest}
    >
      {url ? (
        <ShareableLinkCreated
          url={url}
          onViewAll={() => {
            drawer.setOpen(false);
            navigate(getDataRoomRoute(dataRoomId, 'shareable-links'));
          }}
        />
      ) : (
        renderForm && (
          <Form<CreateDataRoomShareableLinkForm>
            initialValues={initialValues}
            onSubmit={onSubmit}
            validate={validate}
            render={({ values, submitting, invalid, handleSubmit }) => {
              const {
                linkAccess,
                linkExpires,
                anyoneWithNonPublicDomain,
                recipientEmailAddresses,
                recipientEmailAddressesUnsubmited,
              } = values;

              const isAccessSpecificRecipiens =
                getIsAccessAddSpecificRecipiens(linkAccess);
              const hasSpecificRecipiens =
                isAccessSpecificRecipiens &&
                !!getArrayOfEmails(
                  recipientEmailAddresses,
                  recipientEmailAddressesUnsubmited,
                )?.length;

              return (
                <form onSubmit={handleSubmit}>
                  {submitting && <Loader />}

                  <p className="drawer-title">{title}</p>

                  <FileInfo
                    isLoading={isLoadingNodes}
                    isEmpty={isEmpty}
                    nodes={nodes}
                    linkId={link?._id}
                    checkedFolderIds={checkedFolderIds}
                    checkedFileIds={checkedFileIds}
                    onFilesReselect={() => onFilesReselect(checkedFileIds)}
                    updateSecureLinkNodes={updateSecureLinkNodes}
                  />

                  <Field
                    label={t('nameThisLink')}
                    description={t('nameHelpIdentify')}
                    name="name"
                    required
                    component={InputField}
                    validate={validations.required()}
                  />

                  <div className="border" />

                  <p className="subtitle">{t('linkAccess')}</p>

                  <div className="column">
                    <Field<string>
                      name="linkAccess"
                      type="radio"
                      value={DATA_ROOM_SHAREABLE_LINK_ACCESS.ANYONE_WITH_LINK}
                      label={t('anyoneWithTheLink')}
                      component={RadioButtonField}
                    />
                    {getIsAccessAnyone(linkAccess) && (
                      <Field
                        name="recipientEmailAddresses"
                        label={t('emailAddressesInform')}
                        component={TextareaEmailsField}
                      />
                    )}

                    <Field<string>
                      name="linkAccess"
                      type="radio"
                      value={DATA_ROOM_SHAREABLE_LINK_ACCESS.ADD_PASSWORD}
                      label={t('requirePassword')}
                      component={RadioButtonField}
                    />
                    {getIsAccessAddPasword(linkAccess) && (
                      <Field
                        name="password"
                        label={t('password')}
                        withoutStrengthIndicator
                        autoComplete="new-password"
                        component={PasswordField}
                        validate={commonValidations.password}
                        required
                      />
                    )}

                    <Field<string>
                      name="linkAccess"
                      type="radio"
                      value={
                        DATA_ROOM_SHAREABLE_LINK_ACCESS.SPECIFIC_RECIPIENTS
                      }
                      component={RadioButtonField}
                      label={t('specificRecipientsOnly')}
                    />

                    {isAccessSpecificRecipiens && (
                      <>
                        <Field
                          name="recipientEmailAddresses"
                          label={t('recipientEmailAddress')}
                          component={TextareaEmailsField}
                          {...(!anyoneWithNonPublicDomain && {
                            required: true,
                            validate: validations.required(),
                          })}
                        />

                        <Field
                          name="anyoneWithNonPublicDomain"
                          type="checkbox"
                          component={CheckboxField}
                          label={
                            <span>
                              {t('anyoneWithNonPublicDomain')}
                              <ResolvePopup
                                onHover
                                popupContent={t(
                                  'recepientMustBeCustomEmailDomain',
                                )}
                              >
                                <InformationcircleIcon />
                              </ResolvePopup>
                            </span>
                          }
                        />

                        {anyoneWithNonPublicDomain && (
                          <Field
                            label={t('emailDomain')}
                            name="emailDomain"
                            required
                            component={InputField}
                            validate={validations.composeValidators(
                              validations.required(),
                              validations.nonPublicEmailDomainValidation(
                                t('pleaseEnterValidNonPublicEmailDomain'),
                              ),
                            )}
                          />
                        )}

                        <Field
                          name="optionalNote"
                          label={t('includeOptionalNote')}
                          component={TextAreaField}
                          maxLength={1000}
                          rows="4"
                        />
                      </>
                    )}
                  </div>

                  <div className="border" />

                  <div className="column">
                    <RadioGroupField
                      name="linkExpires"
                      label={t('linkExpiresAfter')}
                      options={expireAfterOptions}
                    />

                    {getIsLinkExpiresOther(linkExpires) && (
                      <Field
                        name="linkExpiresDate"
                        component={DatepickerField}
                        onlyFuture
                        required
                        validate={validations.composeValidators(
                          validations.required(),
                          validations.onlyFuture(t('onlyFuture')),
                        )}
                      />
                    )}
                  </div>

                  {!getIsAccessAddSpecificRecipiens(linkAccess) && (
                    <>
                      <div className="border" />
                      <RadioGroupField
                        name="linkDestination"
                        label={t('linkDestination')}
                        options={linkDestinationOptions}
                      />
                    </>
                  )}

                  <div className="border" />

                  <div className="column">
                    <Field
                      name="allowDownloads"
                      type="checkbox"
                      className="green-check-toggle"
                      component={CheckToggleField}
                      label={
                        <span>
                          {t('allowDownloads')}
                          <ResolvePopup
                            onHover
                            popupContent={t('allowRecipientsToDownload')}
                          >
                            <InformationcircleIcon />
                          </ResolvePopup>
                        </span>
                      }
                    />
                  </div>

                  <div className="border" />

                  <ShareableLinkNotifications />

                  <div className="border" />

                  <p className="title">{t('accessProtection')}</p>

                  <ShareableLinkTermsAndConditions key={link?._id} />

                  <ShareableLinkNda />

                  <div className="actions">
                    <Button
                      disabled={submitting}
                      onClick={() => {
                        scrollToErrorsOnSubmit(invalid, handleSubmit);
                      }}
                    >
                      {t(
                        isEdit
                          ? 'editSecureLink'
                          : hasSpecificRecipiens
                          ? 'sendToRecipients'
                          : 'createSecureLink',
                      )}
                    </Button>
                  </div>
                </form>
              );
            }}
          />
        )
      )}
    </SideDrawer>
  );
};

export default forwardRef(ShareableLinkDrawer);
